import React, { useState, FC } from "react";
import { CompanyLocation, objectId, SimpleLatLng } from "@mysnowscape/snow-lib";

import axiosCompanyLocations from "../../../../services/axios/companyLocations";
import SubtleButton from "../../../../components/Buttons/SubtleButton";
import CompanyLocationForm, {
  CompanyLocationFormValues,
} from "../../../../components/CompanyLocations/CompanyLocationForm";
import flash from "../../../../services/flash";

interface CompanyLocationsProps {
  companyId: string;
  initialLocations?: CompanyLocation[];
}

interface LocationResponse {
  locations?: CompanyLocation[];
}

const CompanyLocations: FC<CompanyLocationsProps> = ({
  companyId,
  initialLocations,
}) => {
  const [companyLocations, setCompanyLocations] = useState<CompanyLocation[]>(
    initialLocations || [],
  );
  const [showAddLocation, setShowAddLocation] = useState<boolean>(false);
  const [editingLocationId, setEditingLocationId] = useState<string | null>(
    null,
  );
  const [saving, setSaving] = useState(false);

  const editingLocation = editingLocationId
    ? companyLocations.find((l) => l._id.toString() === editingLocationId)
    : null;

  const resetForm = (): void => {
    setEditingLocationId(null);
    setShowAddLocation(false);
  };

  const handleSaveLocation = (values: CompanyLocationFormValues): void => {
    let locationData: CompanyLocation = {
      _id: editingLocationId || "",
      name: values.name,
      address: values.address,
      location: values.location,
      primary: editingLocationId
        ? companyLocations.find((l) => l._id === editingLocationId)?.primary ??
          false
        : false,
    };
    if (locationData._id === null || locationData._id === "") {
      locationData._id = objectId.newObjectId();
    }

    setSaving(true);
    axiosCompanyLocations.addLocation(
      { companyId, ...locationData },
      // @ts-expect-error: dependency not typed yet
      (response: LocationResponse): void => {
        const updatedLocations: CompanyLocation[] =
          response.locations ||
          (editingLocationId
            ? companyLocations.map((l) =>
                l._id === editingLocationId ? locationData : l,
              )
            : [...companyLocations, locationData]);
        setCompanyLocations(updatedLocations);
        flash.success(
          editingLocationId ? "Location updated" : "Location added",
        );
        setSaving(false);
        resetForm();
      },
      (): void => {
        setSaving(false);
        flash.error("Failed to save location");
      },
    );
  };

  const handleDeleteLocation = (locationId: string): void => {
    if (!window.confirm("Are you sure you want to delete this location?")) {
      return;
    }
    axiosCompanyLocations.removeLocation(
      companyId,
      locationId,
      (): void => {
        setCompanyLocations(
          companyLocations.filter(
            (location) => location._id.toString() !== locationId,
          ),
        );
        flash.success("Location deleted");
      },
      (): void => {
        flash.error("Failed to delete location");
      },
    );
  };

  const handleMarkPrimary = async (locationId: string): Promise<void> => {
    const location = companyLocations.find(
      (l) => l._id.toString() === locationId,
    );
    if (!location) return;

    const updatedLocation: CompanyLocation = { ...location, primary: true };

    axiosCompanyLocations.addLocation(
      { companyId, ...updatedLocation },
      // @ts-expect-error: dependency not typed yet
      (response: LocationResponse): void => {
        const updatedLocations: CompanyLocation[] =
          response.locations ||
          companyLocations.map((l) => ({
            ...l,
            primary: l._id === locationId,
          }));
        setCompanyLocations(updatedLocations);
        flash.success("Primary location updated");
      },
      (): void => {
        flash.error("Failed to update primary location");
      },
    );
  };

  const handleEditLocation = (location: CompanyLocation): void => {
    setEditingLocationId(location._id?.toString());
    setShowAddLocation(true);
  };

  return (
    <div className="mb-4 w-full">
      <label className="mb-2 block text-sm font-bold">Company Locations</label>
      <p className="mb-3 text-xs text-gray-500">
        Set up saved locations (main office, depots, branch locations, etc) for
        start/end points on your company routes. Locations are also used to
        center maps on your service area.
      </p>

      {companyLocations.length > 0 && (
        <div className="mb-4 w-full rounded-md bg-gray-50 p-3">
          <ul className="space-y-2">
            {companyLocations.map((location) => (
              <li
                key={location._id.toString()}
                className="flex items-center justify-between rounded border border-gray-200 bg-white p-3"
              >
                <div className="flex-1">
                  <div className="text-sm font-semibold">
                    {location.name}
                    {location.primary && (
                      <span className="ml-2 rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">
                        Primary
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600">{location.address}</p>
                  <p className="text-xs text-gray-500">
                    {location.location?.lat?.toFixed(4)},{" "}
                    {location.location?.lng?.toFixed(4)}
                  </p>
                </div>
                <div className="ml-3 flex gap-2">
                  <SubtleButton
                    buttonRole="danger"
                    onClick={() =>
                      handleDeleteLocation(location._id.toString())
                    }
                  >
                    Delete
                  </SubtleButton>
                  {!location.primary && (
                    <SubtleButton
                      onClick={() => handleMarkPrimary(location._id.toString())}
                    >
                      Mark Primary
                    </SubtleButton>
                  )}
                  <SubtleButton onClick={() => handleEditLocation(location)}>
                    Edit
                  </SubtleButton>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!showAddLocation ? (
        <SubtleButton onClick={() => setShowAddLocation(true)}>
          + Add Location
        </SubtleButton>
      ) : (
        <CompanyLocationForm
          mode={editingLocationId ? "edit" : "create"}
          initialName={editingLocation?.name ?? ""}
          initialAddress={editingLocation?.address ?? ""}
          initialLocation={
            (editingLocation?.location as SimpleLatLng | undefined) ?? null
          }
          saving={saving}
          onSave={handleSaveLocation}
          onCancel={resetForm}
          saveLabel={editingLocationId ? "Update Location" : "Add Location"}
        />
      )}
    </div>
  );
};

export default CompanyLocations;
